import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, timeAgo } from '@ampersandbrands/ui';
import { useStoreDetails } from '@/lib/useStoreDetails';
import { useHomeSummary, type Broadcast, type HomeSummary } from '@/lib/useHomeSummary';
import { useEffectiveStoreNo } from '@/lib/useStores';
import { isBuilt } from '@/lib/featureFlags';
import { TabletAppStrip } from '@/components/TabletAppStrip';
import { NewsletterCard } from '@/components/NewsletterCard';
import { useNewsletter } from '@/lib/useNewsletter';
import {
  Award,
  Camera,
  CalendarCheck,
  ChevronRight,
  GraduationCap,
  Image,
  Lock,
  Mail,
  Megaphone,
  MessageCircleQuestion,
  Package,
  Plus,
  TrendingUp,
  X,
} from 'lucide-react';

// --- Hero cards -------------------------------------------------------------
// Home is a stack of hero cards. Each eligible tool contributes one card that
// shows its key info (from the existing home-summary data) and links into the
// tool. Users pin/unpin which cards show. v1 persists the pin set in
// localStorage; TODO: move to a server-side per-user prefs endpoint so the
// choice follows a user across the shared back-of-house tablet and their phone.

type HeroStatus = { text: string; attention?: boolean };

type HeroDef = {
  key: string;
  label: string;
  icon: ReactNode;
  to: string;
  status: (s: HomeSummary | null | undefined) => HeroStatus;
};

const plural = (n: number) => (n === 1 ? '' : 's');

const HERO_DEFS: HeroDef[] = [
  {
    key: 'today',
    label: 'Today',
    icon: <CalendarCheck size={20} aria-hidden />,
    to: '/today',
    status: (s) => {
      const n = s?.today?.pending_phases ?? 0;
      return n > 0
        ? { text: `${n} check-in${plural(n)} left today`, attention: true }
        : { text: 'All check-ins done' };
    },
  },
  {
    key: 'inventory',
    label: 'Inventory',
    icon: <Package size={20} aria-hidden />,
    to: '/inventory',
    status: () => ({ text: 'Shelf counts & receiving' }),
  },
  {
    key: 'questions',
    label: 'Questions',
    icon: <MessageCircleQuestion size={20} aria-hidden />,
    to: '/questions',
    status: (s) => {
      const n = s?.questions_open ?? 0;
      return n > 0
        ? { text: `${n} open question${plural(n)}`, attention: true }
        : { text: 'No open questions' };
    },
  },
  {
    key: 'performance',
    label: 'Performance',
    icon: <TrendingUp size={20} aria-hidden />,
    to: '/performance',
    status: () => ({ text: 'Sales, traffic & conversion' }),
  },
  {
    key: 'promotions',
    label: 'Promotions',
    icon: <Megaphone size={20} aria-hidden />,
    to: '/promotions',
    status: (s) => {
      const n = s?.unread?.promotions ?? 0;
      return n > 0 ? { text: `${n} new`, attention: true } : { text: "This week's push" };
    },
  },
  {
    key: 'training',
    label: 'Resources',
    icon: <GraduationCap size={20} aria-hidden />,
    to: '/resources',
    status: (s) => {
      const n = s?.unread?.training ?? 0;
      return n > 0
        ? { text: `${n} new course${plural(n)}`, attention: true }
        : { text: 'Onboarding & courses' };
    },
  },
  {
    key: 'recognition',
    label: 'Recognition',
    icon: <Award size={20} aria-hidden />,
    to: '/recognition',
    status: (s) => {
      const n = s?.unread?.recognition ?? 0;
      return n > 0 ? { text: `${n} new`, attention: true } : { text: 'Shout-outs & ideas' };
    },
  },
];

// Sensible starter set so Home is never empty on first load.
// TODO: derive per role once role-based defaults are wired.
const DEFAULT_PINS = ['today', 'inventory', 'questions', 'performance', 'promotions'];
const PINS_KEY = 'sweetspot:home:pinned-cards';

function loadPins(): string[] {
  try {
    const raw = localStorage.getItem(PINS_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter((x): x is string => typeof x === 'string');
    }
  } catch {
    // ignore malformed storage
  }
  return DEFAULT_PINS;
}

function HeroCard({
  def,
  summary,
  onUnpin,
}: {
  def: HeroDef;
  summary: HomeSummary | null | undefined;
  onUnpin: () => void;
}) {
  const st = def.status(summary);
  return (
    <Card padded={false} className="relative">
      <Link to={def.to} className="flex items-center gap-3 p-4 pr-12">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface-raised text-accent">
          {def.icon}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-semibold text-ink">{def.label}</span>
          <span className={`block text-sm ${st.attention ? 'text-accent' : 'text-ink-muted'}`}>
            {st.text}
          </span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
      </Link>
      <button
        type="button"
        onClick={onUnpin}
        aria-label={`Remove ${def.label} from home`}
        className="absolute right-1 top-1 flex h-11 w-11 items-center justify-center rounded-full text-ink-muted hover:text-ink"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </Card>
  );
}

// --- Legacy tile (still used by the Store links section) --------------------

type Tile = {
  key: string;
  label: string;
  icon: ReactNode;
  href?: string;
  externalDisabled?: boolean;
  loading?: boolean;
};

function TileCard({ tile }: { tile: Tile }) {
  const inner = (
    <>
      <span className="relative inline-flex h-11 w-11 items-center justify-center rounded-full bg-candy-cream-warm text-candy-pink dark:bg-candy-dark-surface-warm dark:text-candy-pink-dark">
        {tile.icon}
        {tile.externalDisabled && (
          <span
            className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-candy-ink-muted text-white dark:bg-candy-dark-ink-muted dark:text-candy-dark-bg"
            aria-hidden
          >
            <Lock size={11} />
          </span>
        )}
      </span>
      <span className="text-[13px] font-medium text-candy-ink dark:text-candy-dark-ink">{tile.label}</span>
    </>
  );

  const className =
    'flex flex-col items-center gap-2 rounded-xl border border-candy-line bg-candy-white px-3 py-4 text-center shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface';

  if (tile.loading) {
    return (
      <div className={`${className} opacity-50`} aria-busy="true">
        {inner}
      </div>
    );
  }

  if (tile.href) {
    return (
      <a
        href={tile.href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${className} hover:bg-candy-cream-warm dark:hover:bg-candy-dark-surface-warm`}
      >
        {inner}
      </a>
    );
  }

  return (
    <div
      className={`${className} cursor-not-allowed opacity-60`}
      title="Not set up for this store yet"
      aria-disabled="true"
    >
      {inner}
    </div>
  );
}

function BroadcastCard({ bc }: { bc: Broadcast }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = bc.body.length > 180 || bc.body.includes('\n');
  return (
    <div className="rounded-xl border border-candy-line bg-candy-white p-4 shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
      <div className="mb-1 flex items-start justify-between gap-2">
        <p className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">{bc.title}</p>
        <span className="mt-0.5 shrink-0 text-[11px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
          {timeAgo(bc.created_at)}
        </span>
      </div>
      <p className={`text-sm text-candy-ink dark:text-candy-dark-ink${!expanded && isLong ? ' line-clamp-3' : ''}`}>
        {bc.body}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[12px] text-candy-teal hover:underline dark:text-candy-teal-dark"
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
}

export function Home() {
  const storeNo = useEffectiveStoreNo();
  const { data: details, isLoading: detailsLoading } = useStoreDetails(storeNo);
  const { data: summary } = useHomeSummary();
  // The weekly newsletter. Its own call rather than a field on the home
  // summary, because it changes once a week and the summary is refetched on
  // every window focus.
  const { data: newsletter } = useNewsletter(storeNo);

  const [pins, setPins] = useState<string[]>(() => loadPins());
  const savePins = (next: string[]) => {
    setPins(next);
    try {
      localStorage.setItem(PINS_KEY, JSON.stringify(next));
    } catch {
      // ignore storage write failures (private mode, quota)
    }
  };

  const pinnedDefs = HERO_DEFS.filter((d) => pins.includes(d.key));
  const unpinnedDefs = HERO_DEFS.filter((d) => !pins.includes(d.key));

  const broadcasts = [...(summary?.broadcasts ?? [])].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  const gmailAddress = details?.editable?.gmail_address;
  const gmailHref = gmailAddress
    ? `https://accounts.google.com/AccountChooser?Email=${encodeURIComponent(gmailAddress)}&continue=${encodeURIComponent('https://mail.google.com/mail/')}`
    : undefined;

  const externalTiles: Tile[] = [
    {
      key: 'cameras',
      label: 'Cameras',
      icon: <Camera size={22} aria-hidden />,
      loading: detailsLoading,
      href: details?.editable?.camera_url ?? undefined,
      externalDisabled: !detailsLoading && !details?.editable?.camera_url,
    },
    {
      key: 'store-photos',
      label: 'Store Photos',
      icon: <Image size={22} aria-hidden />,
      loading: detailsLoading,
      href: details?.editable?.photos_url ?? undefined,
      externalDisabled: !detailsLoading && !details?.editable?.photos_url,
    },
    {
      key: 'store-gmail',
      label: 'Store Gmail',
      icon: <Mail size={22} aria-hidden />,
      loading: detailsLoading,
      href: gmailHref,
      externalDisabled: !detailsLoading && !gmailAddress,
    },
  ];

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">Welcome</h1>
        <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
          Everything for your store, in one place.
        </p>
      </div>

      <TabletAppStrip />

      {newsletter && <NewsletterCard issue={newsletter} storeNo={storeNo} />}

      {broadcasts.length > 0 && (
        <>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted">
            From HQ
          </div>
          <div className="mb-6 space-y-3">
            {broadcasts.map((bc) => (
              <BroadcastCard key={bc.id} bc={bc} />
            ))}
          </div>
        </>
      )}

      <div className="space-y-3">
        {pinnedDefs.map((def) => (
          <HeroCard
            key={def.key}
            def={def}
            summary={summary}
            onUnpin={() => savePins(pins.filter((k) => k !== def.key))}
          />
        ))}
      </div>

      {unpinnedDefs.length > 0 && (
        <div className="mt-6">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted">
            Add to home
          </div>
          <div className="flex flex-wrap gap-2">
            {unpinnedDefs.map((def) => (
              <button
                key={def.key}
                type="button"
                onClick={() => savePins([...pins, def.key])}
                className="inline-flex items-center gap-1.5 rounded-full border border-candy-line bg-candy-white px-3 py-2 text-sm text-candy-ink hover:border-candy-pink hover:bg-candy-cream dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink dark:hover:border-candy-pink-dark dark:hover:bg-candy-dark-surface-warm"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden /> {def.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Store links hidden while Cameras / Store Photos / Store Gmail are not
          built (lib/featureFlags.ts NOT_BUILT). Section returns when any is built. */}
      {externalTiles.filter((t) => isBuilt(t.key)).length > 0 && (
        <>
          <div className="mb-2 mt-6 text-xs font-medium uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted">
            Store links
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {externalTiles
              .filter((t) => isBuilt(t.key))
              .map((tile) => (
                <TileCard key={tile.key} tile={tile} />
              ))}
          </div>
        </>
      )}
    </div>
  );
}
